import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useIntl } from 'react-intl';

import { serveJiraNINFpsNLQNavigation } from '../../common/utils/experiments/experiment-exports';
import { INTENT_DETECTION } from '../../common/utils/intent-detection';
import {
	onAdvancedSearchLinkSelected,
	getAdvancedSearchActionSubjectId,
} from '../../common/utils/quick-find/events/advanced-search-link-selected';
import { onQuickFindClearButtonClicked } from '../../common/utils/quick-find/events/clear-button-clicked';
import { onQuickFindConnectAppsPillClicked } from '../../common/utils/quick-find/events/connect-apps-pill-clicked';
import { onGhostTextShownEvent } from '../../common/utils/quick-find/events/ghost-text-shown';
import { onSearchDialogInputClicked } from '../../common/utils/quick-find/events/search-dialog-input-clicked';
import { onTextEntered } from '../../common/utils/quick-find/events/text-entered';
import { State } from '../../common/utils/quick-find/state';
import { useDebouncedCallback } from '../../common/utils/use-debounced-callback';
import {
	useQuickFindAnalytics,
	useQuickFindAttributes,
} from '../../controllers/quick-find/analytics';
import { usePrimaryProduct } from '../../controllers/store/bootstrap';
import { useContent, useQuickFindOpen } from '../../controllers/store/quick-find';

const ON_TEXT_ENTERED_ANALYTICS_DEBOUNCE_TIME = 300;

export const useDebouncedOnTextEnteredAnalyticsCallback: () => import('lodash').DebouncedFunc<
	() => void
> = () => {
	const quickFindOpen = useQuickFindOpen();
	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useQuickFindAttributes();
	const fireOnTextEnteredAnalyticsCallback = useCallback(() => {
		if (!quickFindOpen) {
			return;
		}
		fireAnalyticsEvent(onTextEntered({ attributes: commonAttributes, nonPrivacySafeAttributes }));
	}, [commonAttributes, fireAnalyticsEvent, nonPrivacySafeAttributes, quickFindOpen]);
	const debouncedOnTextEnteredAnalyticsCallback = useDebouncedCallback(
		fireOnTextEnteredAnalyticsCallback,
		ON_TEXT_ENTERED_ANALYTICS_DEBOUNCE_TIME,
	);
	useEffect(
		() => () => debouncedOnTextEnteredAnalyticsCallback.cancel(),
		[debouncedOnTextEnteredAnalyticsCallback],
	);
	return debouncedOnTextEnteredAnalyticsCallback;
};

type SubmitInteraction = { query: string; trigger: 'return' | 'click'; returnModified: boolean };

export type QuickFindInputAnalytics = {
	onPointerDown: () => void;
	fireOnSearchDialogInputClicked: () => void;
	debouncedOnTextEnteredAnalyticsCallback: import('lodash').DebouncedFunc<() => void>;
	trackSearchSubmit: (interaction: SubmitInteraction) => void;
	fireOnQuickFindClearButtonClicked: () => void;
	fireOnQuickFindConnectAppsPillClicked: () => void;
	fireOnGhostTextShownEvent: (attributes: {
		id: string;
		locale: string;
		customGhostText: string;
	}) => void;
};

export const useQuickFindInputAnalytics = (): QuickFindInputAnalytics => {
	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useQuickFindAttributes();
	const [primaryProduct] = usePrimaryProduct();
	const content = useContent();
	const quickFindOpen = useQuickFindOpen();
	const openBeforePointerDown = useRef<boolean | undefined>(undefined);
	// Capture before focus can open a host-owned dropdown.
	const onPointerDown = useCallback(() => {
		openBeforePointerDown.current = quickFindOpen;
	}, [quickFindOpen]);
	const fireOnSearchDialogInputClicked = useCallback(() => {
		const wasOpen = openBeforePointerDown.current ?? quickFindOpen;
		openBeforePointerDown.current = undefined;
		if (!wasOpen) {
			fireAnalyticsEvent(
				onSearchDialogInputClicked({
					attributes: { ...commonAttributes, productKey: primaryProduct },
					nonPrivacySafeAttributes,
				}),
			);
		}
	}, [
		quickFindOpen,
		fireAnalyticsEvent,
		commonAttributes,
		primaryProduct,
		nonPrivacySafeAttributes,
	]);
	const debouncedOnTextEnteredAnalyticsCallback = useDebouncedOnTextEnteredAnalyticsCallback();
	const fireOnQuickFindClearButtonClicked = useCallback(() => {
		debouncedOnTextEnteredAnalyticsCallback.cancel();
		fireAnalyticsEvent(
			onQuickFindClearButtonClicked({ attributes: commonAttributes, nonPrivacySafeAttributes }),
		);
	}, [
		debouncedOnTextEnteredAnalyticsCallback,
		fireAnalyticsEvent,
		commonAttributes,
		nonPrivacySafeAttributes,
	]);
	const fireOnQuickFindConnectAppsPillClicked = useCallback(() => {
		fireAnalyticsEvent(
			onQuickFindConnectAppsPillClicked({ attributes: commonAttributes, nonPrivacySafeAttributes }),
		);
	}, [fireAnalyticsEvent, commonAttributes, nonPrivacySafeAttributes]);
	const fireOnGhostTextShownEvent = useCallback(
		(attributes: { id: string; locale: string; customGhostText: string }) => {
			fireAnalyticsEvent(onGhostTextShownEvent({ attributes }));
		},
		[fireAnalyticsEvent],
	);
	const { locale } = useIntl();
	const intentDetectionExperiment = serveJiraNINFpsNLQNavigation({
		primaryProduct,
		locale,
		fireLayerExposure: false,
	});
	const intentDetectionCohort = intentDetectionExperiment?.cohort;

	const trackSearchSubmit = useCallback(
		({ query, trigger, returnModified }: SubmitInteraction) => {
			if (!primaryProduct) {
				return;
			}
			fireAnalyticsEvent(
				onAdvancedSearchLinkSelected({
					actionSubjectId: getAdvancedSearchActionSubjectId(primaryProduct),
					query,
					intent: INTENT_DETECTION.value(),
					isLoading:
						content?.state === State.PREQUERY_LOADING || content?.state === State.POSTQUERY_LOADING,
					cohort: intentDetectionCohort,
					attributes: {
						...commonAttributes,
						trigger,
						returnModified,
						newTab: false,
					},
					nonPrivacySafeAttributes,
				}),
			);
		},
		[
			nonPrivacySafeAttributes,
			intentDetectionCohort,
			commonAttributes,
			content?.state,
			fireAnalyticsEvent,
			primaryProduct,
		],
	);

	return useMemo(
		() => ({
			onPointerDown,
			fireOnSearchDialogInputClicked,
			debouncedOnTextEnteredAnalyticsCallback,
			trackSearchSubmit,
			fireOnQuickFindClearButtonClicked,
			fireOnQuickFindConnectAppsPillClicked,
			fireOnGhostTextShownEvent,
		}),
		[
			onPointerDown,
			fireOnSearchDialogInputClicked,
			debouncedOnTextEnteredAnalyticsCallback,
			trackSearchSubmit,
			fireOnQuickFindClearButtonClicked,
			fireOnQuickFindConnectAppsPillClicked,
			fireOnGhostTextShownEvent,
		],
	);
};
